<template>
  <DashboardLayout>
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-3xl font-bold text-gray-900">Ticket Reports</h1>
          <p class="text-gray-600 mt-1">Analytics and insights for ticket management</p>
        </div>
        <div class="flex items-center space-x-3">
          <Button variant="outline" @click="exportCSV" :loading="exportingCSV">
            <ArrowDownTrayIcon class="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" @click="exportPDF" :loading="exportingPDF">
            <DocumentArrowDownIcon class="h-4 w-4 mr-2" />
            Export PDF
          </Button>
        </div>
      </div>

      <!-- Date Range Filter -->
      <Card class="mb-6">
        <div class="flex flex-wrap items-end gap-4">
          <div class="flex-1 min-w-[200px]">
            <label class="block text-sm font-medium text-gray-700 mb-2">From Date</label>
            <Input
              v-model="filters.date_from"
              type="date"
              @change="applyFilters"
            />
          </div>
          <div class="flex-1 min-w-[200px]">
            <label class="block text-sm font-medium text-gray-700 mb-2">To Date</label>
            <Input
              v-model="filters.date_to"
              type="date"
              @change="applyFilters"
            />
          </div>
          <div class="flex space-x-2">
            <Button @click="setDateRange('today')" variant="outline" size="sm">
              Today
            </Button>
            <Button @click="setDateRange('week')" variant="outline" size="sm">
              This Week
            </Button>
            <Button @click="setDateRange('month')" variant="outline" size="sm">
              This Month
            </Button>
            <Button @click="setDateRange('quarter')" variant="outline" size="sm">
              This Quarter
            </Button>
          </div>
        </div>
      </Card>

      <!-- Overview Stats -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Card>
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <TicketIcon class="h-10 w-10 text-primary-600" />
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">Total Tickets</p>
              <p class="text-2xl font-bold text-gray-900">{{ stats.total_tickets }}</p>
              <p class="text-xs text-gray-500 mt-1">
                <span :class="getChangeColor(stats.total_tickets_change)">
                  {{ formatChange(stats.total_tickets_change) }}
                </span>
                vs previous period
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <FolderOpenIcon class="h-10 w-10 text-blue-600" />
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">Open Tickets</p>
              <p class="text-2xl font-bold text-gray-900">{{ stats.open_tickets }}</p>
              <p class="text-xs text-gray-500 mt-1">
                {{ ((stats.open_tickets / stats.total_tickets) * 100 || 0).toFixed(1) }}% of total
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <CheckCircleIcon class="h-10 w-10 text-green-600" />
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">Resolved Today</p>
              <p class="text-2xl font-bold text-gray-900">{{ stats.resolved_today }}</p>
              <p class="text-xs text-gray-500 mt-1">
                {{ ((stats.resolved_today / stats.total_tickets) * 100 || 0).toFixed(1) }}% of total
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <ClockIcon class="h-10 w-10 text-purple-600" />
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">Avg Resolution</p>
              <p class="text-2xl font-bold text-gray-900">{{ stats.avg_resolution_time }}</p>
              <p class="text-xs text-gray-500 mt-1">
                <span :class="getChangeColor(-stats.resolution_time_change)">
                  {{ formatChange(stats.resolution_time_change) }}
                </span>
                vs previous
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <ChartBarIcon class="h-10 w-10 text-green-600" />
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">SLA Compliance</p>
              <p class="text-2xl font-bold text-gray-900">{{ stats.sla_compliance_rate }}%</p>
              <p class="text-xs text-gray-500 mt-1">
                <span :class="getChangeColor(stats.sla_compliance_change)">
                  {{ formatChange(stats.sla_compliance_change) }}
                </span>
                vs previous
              </p>
            </div>
          </div>
        </Card>
      </div>

      <!-- Charts Row 1 -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <!-- Tickets by Status -->
        <Card>
          <h2 class="text-lg font-semibold text-gray-900 mb-4">Tickets by Status</h2>
          <div class="h-80">
            <PieChart
              :data="chartData.byStatus"
              :colors="statusColors"
            />
          </div>
        </Card>

        <!-- Tickets by Priority -->
        <Card>
          <h2 class="text-lg font-semibold text-gray-900 mb-4">Tickets by Priority</h2>
          <div class="h-80">
            <BarChart
              :data="chartData.byPriority"
              :colors="priorityColors"
            />
          </div>
        </Card>
      </div>

      <!-- Charts Row 2 -->
      <div class="grid grid-cols-1 gap-6 mb-6">
        <!-- Tickets Over Time -->
        <Card>
          <h2 class="text-lg font-semibold text-gray-900 mb-4">Tickets Over Time</h2>
          <div class="h-80">
            <LineChart
              :data="chartData.overTime"
              :series="['Created', 'Resolved', 'Closed']"
            />
          </div>
        </Card>
      </div>

      <!-- Charts Row 3 -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <!-- Tickets by Category -->
        <Card>
          <h2 class="text-lg font-semibold text-gray-900 mb-4">Tickets by Category</h2>
          <div class="h-80">
            <BarChart
              :data="chartData.byCategory"
              :horizontal="true"
            />
          </div>
        </Card>

        <!-- Response Time Trends -->
        <Card>
          <h2 class="text-lg font-semibold text-gray-900 mb-4">Response Time Trends</h2>
          <div class="h-80">
            <LineChart
              :data="chartData.responseTrends"
              :series="['Avg Response Time', 'Target']"
              :colors="['#3b82f6', '#ef4444']"
            />
          </div>
        </Card>
      </div>

      <!-- Agent Performance -->
      <Card class="mb-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">Agent Performance</h2>
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="border-b border-gray-200 bg-gray-50">
                <th class="text-left py-3 px-4 font-semibold text-gray-700">Agent</th>
                <th class="text-right py-3 px-4 font-semibold text-gray-700">Assigned</th>
                <th class="text-right py-3 px-4 font-semibold text-gray-700">Resolved</th>
                <th class="text-right py-3 px-4 font-semibold text-gray-700">Avg Response</th>
                <th class="text-right py-3 px-4 font-semibold text-gray-700">Avg Resolution</th>
                <th class="text-right py-3 px-4 font-semibold text-gray-700">Resolution Rate</th>
                <th class="text-right py-3 px-4 font-semibold text-gray-700">SLA Compliance</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr
                v-for="agent in agentPerformance"
                :key="agent.id"
                class="hover:bg-gray-50"
              >
                <td class="py-3 px-4">
                  <div class="flex items-center space-x-2">
                    <Avatar
                      :src="agent.profile_photo"
                      :name="agent.full_name"
                      size="sm"
                    />
                    <span class="font-medium text-gray-900">{{ agent.full_name }}</span>
                  </div>
                </td>
                <td class="text-right py-3 px-4 text-gray-900">{{ agent.assigned_count }}</td>
                <td class="text-right py-3 px-4 text-gray-900">{{ agent.resolved_count }}</td>
                <td class="text-right py-3 px-4 text-gray-900">{{ agent.avg_response_time }}</td>
                <td class="text-right py-3 px-4 text-gray-900">{{ agent.avg_resolution_time }}</td>
                <td class="text-right py-3 px-4">
                  <div class="flex items-center justify-end space-x-2">
                    <div class="w-16 bg-gray-200 rounded-full h-2">
                      <div
                        class="bg-green-600 h-2 rounded-full"
                        :style="{ width: `${agent.resolution_rate}%` }"
                      ></div>
                    </div>
                    <span class="text-sm text-gray-900">{{ agent.resolution_rate }}%</span>
                  </div>
                </td>
                <td class="text-right py-3 px-4">
                  <Badge :variant="agent.sla_compliance >= 90 ? 'success' : agent.sla_compliance >= 70 ? 'warning' : 'danger'">
                    {{ agent.sla_compliance }}%
                  </Badge>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      <!-- Category Analysis -->
      <Card class="mb-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">Category Analysis</h2>
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="border-b border-gray-200 bg-gray-50">
                <th class="text-left py-3 px-4 font-semibold text-gray-700">Category</th>
                <th class="text-right py-3 px-4 font-semibold text-gray-700">Total Tickets</th>
                <th class="text-right py-3 px-4 font-semibold text-gray-700">Avg Resolution Time</th>
                <th class="text-right py-3 px-4 font-semibold text-gray-700">Resolution Rate</th>
                <th class="text-right py-3 px-4 font-semibold text-gray-700">SLA Compliance</th>
                <th class="text-left py-3 px-4 font-semibold text-gray-700">Common Issues</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr
                v-for="category in categoryAnalysis"
                :key="category.id"
                class="hover:bg-gray-50"
              >
                <td class="py-3 px-4">
                  <div class="flex items-center space-x-2">
                    <div
                      class="w-8 h-8 rounded flex items-center justify-center"
                      :style="{ backgroundColor: category.color || '#e5e7eb' }"
                    >
                      <component
                        v-if="category.icon"
                        :is="getIcon(category.icon)"
                        class="h-4 w-4 text-white"
                      />
                    </div>
                    <span class="font-medium text-gray-900">{{ category.name }}</span>
                  </div>
                </td>
                <td class="text-right py-3 px-4 text-gray-900">{{ category.ticket_count }}</td>
                <td class="text-right py-3 px-4 text-gray-900">{{ category.avg_resolution_time }}</td>
                <td class="text-right py-3 px-4">
                  <div class="flex items-center justify-end space-x-2">
                    <div class="w-16 bg-gray-200 rounded-full h-2">
                      <div
                        class="bg-blue-600 h-2 rounded-full"
                        :style="{ width: `${category.resolution_rate}%` }"
                      ></div>
                    </div>
                    <span class="text-sm text-gray-900">{{ category.resolution_rate }}%</span>
                  </div>
                </td>
                <td class="text-right py-3 px-4">
                  <Badge :variant="category.sla_compliance >= 90 ? 'success' : category.sla_compliance >= 70 ? 'warning' : 'danger'">
                    {{ category.sla_compliance }}%
                  </Badge>
                </td>
                <td class="py-3 px-4">
                  <div class="flex flex-wrap gap-1">
                    <Badge
                      v-for="issue in category.common_issues.slice(0, 3)"
                      :key="issue"
                      variant="neutral"
                      size="sm"
                    >
                      {{ issue }}
                    </Badge>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      <!-- SLA Compliance -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Breached Tickets -->
        <Card>
          <h2 class="text-lg font-semibold text-gray-900 mb-4">SLA Breached Tickets</h2>
          <div class="space-y-3">
            <div
              v-for="ticket in slaCompliance.breached_tickets"
              :key="ticket.id"
              class="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg"
            >
              <div class="flex-1 min-w-0">
                <Link
                  :href="route('tickets.show', ticket.id)"
                  class="text-sm font-medium text-gray-900 hover:text-primary-600"
                >
                  {{ ticket.ticket_number }} - {{ ticket.subject }}
                </Link>
                <p class="text-xs text-gray-600 mt-1">
                  <Badge :variant="getPriorityVariant(ticket.priority)" size="sm">
                    {{ formatPriority(ticket.priority) }}
                  </Badge>
                  <span class="ml-2">Breached {{ ticket.breach_time }} ago</span>
                </p>
              </div>
            </div>
            <div v-if="slaCompliance.breached_tickets.length === 0" class="text-center py-8">
              <CheckCircleIcon class="mx-auto h-12 w-12 text-green-400" />
              <p class="mt-2 text-sm text-gray-500">No breached tickets</p>
            </div>
          </div>
        </Card>

        <!-- At-Risk Tickets -->
        <Card>
          <h2 class="text-lg font-semibold text-gray-900 mb-4">At-Risk Tickets</h2>
          <div class="space-y-3">
            <div
              v-for="ticket in slaCompliance.at_risk_tickets"
              :key="ticket.id"
              class="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
            >
              <div class="flex-1 min-w-0">
                <Link
                  :href="route('tickets.show', ticket.id)"
                  class="text-sm font-medium text-gray-900 hover:text-primary-600"
                >
                  {{ ticket.ticket_number }} - {{ ticket.subject }}
                </Link>
                <p class="text-xs text-gray-600 mt-1">
                  <Badge :variant="getPriorityVariant(ticket.priority)" size="sm">
                    {{ formatPriority(ticket.priority) }}
                  </Badge>
                  <span class="ml-2">Due in {{ ticket.time_remaining }}</span>
                </p>
              </div>
            </div>
            <div v-if="slaCompliance.at_risk_tickets.length === 0" class="text-center py-8">
              <CheckCircleIcon class="mx-auto h-12 w-12 text-green-400" />
              <p class="mt-2 text-sm text-gray-500">No at-risk tickets</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed } from 'vue';
import { router, Link } from '@inertiajs/vue3';
import DashboardLayout from '@/Layouts/DashboardLayout.vue';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Badge from '@/Components/Common/Badge.vue';
import Input from '@/Components/Common/Input.vue';
import Avatar from '@/Components/Common/Avatar.vue';
import PieChart from '@/Components/Charts/PieChart.vue';
import BarChart from '@/Components/Charts/BarChart.vue';
import LineChart from '@/Components/Charts/LineChart.vue';
import {
  ArrowDownTrayIcon,
  DocumentArrowDownIcon,
  TicketIcon,
  FolderOpenIcon,
  CheckCircleIcon,
  ClockIcon,
  ChartBarIcon,
  FolderIcon,
} from '@heroicons/vue/24/outline';
import { useToast } from '@/Composables/useToast';

const props = defineProps({
  stats: {
    type: Object,
    required: true,
  },
  chartData: {
    type: Object,
    required: true,
  },
  agentPerformance: {
    type: Array,
    required: true,
  },
  categoryAnalysis: {
    type: Array,
    required: true,
  },
  slaCompliance: {
    type: Object,
    required: true,
  },
  dateRange: {
    type: Object,
    default: () => ({}),
  },
});

const { showToast } = useToast();

// State
const exportingCSV = ref(false);
const exportingPDF = ref(false);

const filters = ref({
  date_from: props.dateRange.date_from || '',
  date_to: props.dateRange.date_to || '',
});

// Chart Colors
const statusColors = ['#3b82f6', '#f59e0b', '#e5e7eb', '#10b981', '#6b7280', '#ef4444'];
const priorityColors = ['#6b7280', '#3b82f6', '#f59e0b', '#ef4444'];

// Methods
const applyFilters = () => {
  router.get(
    route('tickets.reports'),
    {
      date_from: filters.value.date_from,
      date_to: filters.value.date_to,
    },
    {
      preserveState: true,
      preserveScroll: true,
    }
  );
};

const setDateRange = (range) => {
  const today = new Date();
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay());
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const startOfQuarter = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);

  switch (range) {
    case 'today':
      filters.value.date_from = today.toISOString().split('T')[0];
      filters.value.date_to = today.toISOString().split('T')[0];
      break;
    case 'week':
      filters.value.date_from = startOfWeek.toISOString().split('T')[0];
      filters.value.date_to = today.toISOString().split('T')[0];
      break;
    case 'month':
      filters.value.date_from = startOfMonth.toISOString().split('T')[0];
      filters.value.date_to = today.toISOString().split('T')[0];
      break;
    case 'quarter':
      filters.value.date_from = startOfQuarter.toISOString().split('T')[0];
      filters.value.date_to = today.toISOString().split('T')[0];
      break;
  }
  applyFilters();
};

const exportCSV = () => {
  exportingCSV.value = true;
  window.location.href = route('tickets.reports.export', {
    format: 'csv',
    date_from: filters.value.date_from,
    date_to: filters.value.date_to,
  });
  setTimeout(() => {
    exportingCSV.value = false;
    showToast({
      type: 'success',
      message: 'Report exported successfully.',
    });
  }, 2000);
};

const exportPDF = () => {
  exportingPDF.value = true;
  window.location.href = route('tickets.reports.export', {
    format: 'pdf',
    date_from: filters.value.date_from,
    date_to: filters.value.date_to,
  });
  setTimeout(() => {
    exportingPDF.value = false;
    showToast({
      type: 'success',
      message: 'Report exported successfully.',
    });
  }, 2000);
};

const getIcon = (iconName) => {
  return FolderIcon; // Simplified - would map to actual icons
};

const formatChange = (change) => {
  const sign = change >= 0 ? '+' : '';
  return `${sign}${change}%`;
};

const getChangeColor = (change) => {
  if (change > 0) return 'text-green-600';
  if (change < 0) return 'text-red-600';
  return 'text-gray-600';
};

const formatPriority = (priority) => {
  const labels = {
    low: 'Low',
    normal: 'Normal',
    high: 'High',
    urgent: 'Urgent',
  };
  return labels[priority] || priority;
};

const getPriorityVariant = (priority) => {
  const variants = {
    low: 'neutral',
    normal: 'info',
    high: 'warning',
    urgent: 'danger',
  };
  return variants[priority] || 'neutral';
};
</script>
